/**
 * Auth User Button
 *
 * A unified user button that works with any auth provider (Firebase, Native).
 * Shows user avatar with a dropdown menu for account actions.
 */

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, Settings, ChevronDown } from "lucide-react";
import { useAuth } from "@/lib/platform/AuthProvider";
import { useAppNavigation } from "@/lib/platform";
import { useT } from "@/lib/i18n";
import { useProfile } from "@/lib/api/useProfile";
import { ProfileImage } from "@/components/ProfileImage";

interface AuthUserButtonProps {
  /** URL to redirect to after sign out */
  afterSignOutUrl?: string;
  /** Size of the avatar */
  size?: "sm" | "md" | "lg";
  /** Whether to show the dropdown arrow */
  showArrow?: boolean;
}

export default function AuthUserButton({
  afterSignOutUrl = "/",
  size = "md",
  showArrow = false,
}: AuthUserButtonProps) {
  const { t } = useT();
  const { user, signOut, isSignedIn, showAccountDialog, isNative } = useAuth();
  const { data: profileData } = useProfile();
  // The DB profile is the source of truth for the name and image. The auth
  // `user` derives its name from Firebase's displayName (often the email, or
  // unset), so prefer the profile and only fall back to auth.
  const profileUser = profileData?.data?.user as
    | { firstName?: string | null; lastName?: string | null; profileImageUrl?: string | null }
    | undefined;
  const firstName = profileUser?.firstName ?? user?.firstName ?? null;
  const lastName = profileUser?.lastName ?? user?.lastName ?? null;
  const avatarUser = {
    firstName,
    lastName,
    profileImageUrl: profileUser?.profileImageUrl,
  };
  const [isOpen, setIsOpen] = useState(false);
  const { navigate } = useAppNavigation();
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close menu on escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const handleSignOut = async () => {
    setIsOpen(false);
    await signOut();
    navigate(afterSignOutUrl);
  };

  const handleAccountClick = () => {
    setIsOpen(false);
    if (isNative) {
      showAccountDialog();
    } else {
      navigate("/profile");
    }
  };

  if (!isSignedIn) {
    return null;
  }

  // Map this component's size scale onto ProfileImage's. `lg` here historically
  // rendered at 48px; ProfileImage doesn't have an exact match so we use `md`
  // (40px) which is visually closest without going to 64px.
  const profileImageSize = size === "lg" ? "md" : size;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`
          rounded-full
          flex items-center justify-center
          hover:opacity-90
          transition-opacity
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-900
          ${showArrow ? "pr-1" : ""}
        `}
        title={t('common.labels.account')}
      >
        <ProfileImage size={profileImageSize} user={avatarUser} />
        {showArrow && (
          <ChevronDown
            className={`w-3 h-3 ml-1 text-primary-600 dark:text-primary-400 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={menuRef}
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-64 origin-top-right z-50"
          >
            <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg ring-1 ring-black/5 dark:ring-white/10 overflow-hidden">
              {/* User info header */}
              <div className="px-4 py-3 border-b border-gray-100 dark:border-neutral-700">
                <div className="flex items-center gap-3">
                  <ProfileImage size="md" user={avatarUser} />
                  <div className="min-w-0 flex-1">
                    {(firstName || lastName) && (
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {[firstName, lastName].filter(Boolean).join(" ")}
                      </p>
                    )}
                    {user?.email && (
                      <p className="text-xs text-gray-500 dark:text-neutral-400 truncate">
                        {user.email}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Menu items */}
              <div className="py-1">
                <button
                  onClick={handleAccountClick}
                  className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-neutral-200 hover:bg-gray-50 dark:hover:bg-neutral-700/50 flex items-center gap-3 transition-colors"
                >
                  <Settings className="w-4 h-4 text-gray-400 dark:text-neutral-500" />
                  <span>{t('profile.account.manageAccount')}</span>
                </button>

                <button
                  onClick={handleSignOut}
                  className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-neutral-200 hover:bg-gray-50 dark:hover:bg-neutral-700/50 flex items-center gap-3 transition-colors"
                >
                  <LogOut className="w-4 h-4 text-gray-400 dark:text-neutral-500" />
                  <span>{t('profile.account.signOut')}</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

